// file: `react/src/pages/Login/Login.tsx`:

import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Form, Alert, Table } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { expressURL } from '../../utils/networkUtils';
import { useCrypto } from '../../components/CryptoContext.tsx';
import { safeJoinNextPath } from '../../utils/joinPath.ts';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [unconfirmed, setUnconfirmed] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resending, setResending] = useState(false);
  const [lab, setLab] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = safeJoinNextPath(searchParams.get('next'));
  const { loginWithPassword } = useCrypto();

  useEffect(() => {
    let cancelled = false;
    fetch(`${expressURL()}/api/school/lab`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!cancelled && data && data.enabled) setLab(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const handleResend = async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setResendMessage('Enter your email address first.');
      return;
    }
    setResending(true);
    setResendMessage('');
    try {
      const response = await axios.post(`${expressURL()}/auth/resend-confirmation`, {
        email: trimmed,
      });
      setResendMessage(typeof response.data === 'string' ? response.data : 'If that email needs confirmation, we sent a confirmation link.');
    } catch (error) {
      const detail = error.response?.data;
      const text = typeof detail === 'string' ? detail : (detail?.error || error.message || 'unknown error');
      setResendMessage('Error: ' + text);
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUnconfirmed(false);
    setResendMessage('');
    try {
      await loginWithPassword(email.trim(), password);
      navigate(nextPath || '/');
    } catch (error) {
      console.error('Login failed:', error);
      const detail = error.response?.data;
      const text = typeof detail === 'string' ? detail : (detail?.error || error.message || 'unknown error');
      setMessage('Error: ' + text);
      setUnconfirmed(typeof detail === 'string' && detail.toLowerCase().includes('not confirmed'));
    }
  };

  return (
    <div className='main-content'>
      <Container className='mb-5'>
        <h1 className="display-4 mb-3 mt-3">Login</h1>
        <form onSubmit={handleSubmit}>
          <Row>
            <Col sm={3} md={2} xxl={1}>
              <Form.Label>Email address</Form.Label>
            </Col>
            <Col className='mb-3'>
              <Form.Control
                type="email"
                autoComplete="username"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Col>
          </Row>
          <Row>
            <Col sm={3} md={2} xxl={1}>
              <Form.Label>Password</Form.Label>
            </Col>
            <Col className='mb-3'>
              <Form.Control
                type="password"
                autoComplete="current-password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Col>
          </Row>
          <button className='btn btn-primary' type="submit">Login</button>
        </form>
        {message && <p className="mt-3">{message}</p>}
        {unconfirmed && (
          <div className="mt-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={resending}
              onClick={handleResend}
            >
              {resending ? 'Sending…' : 'Resend confirmation email'}
            </button>
            {resendMessage && <p className="mt-2 mb-0">{resendMessage}</p>}
          </div>
        )}
        <p className="mt-3 mb-1"><Link to="/forgot-password">Forgot password</Link></p>
        <p>No account? <Link to={nextPath ? `/signup?next=${encodeURIComponent(nextPath)}` : '/signup'}>Signup</Link></p>
        {lab && (
          <Alert variant="warning" className="mt-4">
            <Alert.Heading as="h2" className="h5">{lab.notice}</Alert.Heading>
            <p className="mb-2">
              Shared password: <code>{lab.sharedPassword}</code>
            </p>
            <Table size="sm" bordered hover responsive className="mb-0 bg-body">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Who</th>
                  <th>Role</th>
                </tr>
              </thead>
              <tbody>
                {lab.accounts.map((account) => (
                  <tr
                    key={account.email}
                    role="button"
                    onClick={() => {
                      setEmail(account.email);
                      setPassword(lab.sharedPassword);
                    }}
                  >
                    <td><code>{account.email}</code></td>
                    <td>{account.fullName}</td>
                    <td>{(account.roles || []).join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <p className="small mt-2 mb-0">
              Click a row to fill the form. Separate browser profiles let you play more than one role.
              {lab.safety?.notice ? ` ${lab.safety.notice}` : ''}
            </p>
          </Alert>
        )}
      </Container>
    </div>
  );
}

export default Login;
